// Scrollable list of messages for one agent.
//
// Sprint 5.6 (D6) — centered column. The inner column is `max-w-3xl
// mx-auto` so prose lines are short enough to read comfortably; the
// surrounding scroll container retains its full-height surface.
//
// Sprint 5.6 (D9) — subagent grouping. Messages with parentToolUseId
// are pulled out of the main timeline and rendered inside the
// SubagentPanel for their parent tool_use.
//
// Sprint 5.6 (D25) — per-turn model badge. We compute the set of
// distinct models the agent has used at least once; if it's ≥2, every
// assistant_text gets a small model badge underneath.

import { useMemo, useCallback, Fragment } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../i18n';
import type { Message } from '../types';
import { useSettingsStore } from '../stores/settings.store';
import {
  distinctModelsInHistory,
  groupBySubagent,
  pairToolResults,
  pairTaskNotifications,
  asToolUse,
  SUBAGENT_TOOL_NAMES,
  WORKFLOW_TOOL_NAMES,
  type BackgroundLaunchCtx,
} from '../lib/messages';
import { DeveloperOnly } from './founder/DeveloperOnly';
import { classifyNoiseRow, type NoiseVerdict } from '../lib/tool-rendering/agent-noise';
import { InternalReportRow } from './founder/InternalReportRow';
import { aggregateMessages, type RenderItem } from '../lib/tool-rendering/aggregate';
import { synthesizeTurnSummaries } from '../lib/tool-rendering/synthesize-summary';
import {
  collectTodoEmits as collectTodoEmitsLocal,
  deriveProgress,
  parseTodos,
} from '../lib/tool-rendering/derive-progress';
import { diffTodos } from '../lib/tool-rendering/diff-todos';
import {
  deriveTurnMeta,
  splitWorkSegments,
  buildCollapseSignature,
  LEAD_TURN_ID,
  TURN_STALENESS_SUBTYPES,
  TURN_CLOSING_SUBTYPES,
  type WorkSegment as WorkSegmentData,
} from '../lib/tool-rendering/turn-boundaries';
import { formatWorkedFor } from '../lib/tool-rendering/format-elapsed';
import { useChatScroll } from '../hooks/useChatScroll';
import { useCollapseAnchor } from '../hooks/useCollapseAnchor';
import { useInfiniteScrollUp } from '../hooks/useInfiniteScrollUp';
import { usePaginatedHistory } from '../hooks/usePaginatedHistory';
import { useAppStore } from '../stores/app.store';
import { MessageBubble } from './MessageBubble';
import { PartialIndicator } from './PartialIndicator';
import { StreamTruncatedPill } from './StreamTruncatedPill';
import { LiveActivity, WorkingFallback, isAwaitingHelpers } from './LiveActivity';
import type { RowLivenessCtx } from '../lib/tool-rendering/founder-layer';
import { ScrollToBottomPill } from './ScrollToBottomPill';
import { MessageRail } from './founder/message-rail/MessageRail';
import { SubagentPanel } from './SubagentPanel';
import { WorkflowPanel } from './WorkflowPanel';
import { DispatchWorkerCard } from './DispatchWorkerCard';
import { AggregationCard } from './founder/AggregationCard';
import { TurnCollapse } from './founder/TurnCollapse';
import { WorkSegment } from './founder/WorkSegment';
import { StickyProgressBar } from './founder/StickyProgressBar';
// S6.5 — permission card cluster.
import { PermissionCard } from './permissions/PermissionCard';
import { AskUserQuestionCard } from './permissions/AskUserQuestionCard';
import {
  getRequestId,
  isAskUserQuestionPending,
  isPermissionPending,
  isPermissionTerminal,
  type AskUserQuestionPendingContent,
  type PermissionPendingContent,
  type PermissionTerminalContent,
} from '../lib/permissions/types';

export interface ConversationListProps {
  agentId: string;
  messages: Message[];
  // Sprint messages-section-fixes (D-msg-1) — Edit / Resend wiring.
  // ChatView supplies these handlers; ConversationList passes them
  // through to user-message bubbles (regular + sticky). When undefined
  // (e.g. read-only history view) the bubbles render without action
  // buttons, exactly as before this sprint.
  onEditMessage?: (text: string) => void;
  onResendMessage?: (text: string) => void;
}

// Sprint 12 (D-008) — system message subtypes hidden in founder mode.
const HIDDEN_SYSTEM_SUBTYPES = new Set([
  'init', 'turn_boundary', 'result', 'exit',
  'stopped_by_user', 'respawning', 'respawned',
  'reconciled', 'crash', 'stuck', 'recovered',
]);
// `task_notification` is NOT in this set on purpose (fix round, FM7/QA 2026-09-13):
// `HIDDEN_SYSTEM_SUBTYPES` only applies `if (!developerMode …)` below, and D5's intent
// ("nunca píldora") is unconditional on mode. It is filtered unconditionally instead,
// the SAME way `workflow_progress` is (see the `renderItems` filter below) — one real
// filter site, not two overlapping ones for the same subtype.

function getTodoInput(m: Message): Record<string, unknown> {
  const c = m.content;
  if (typeof c === 'object' && c !== null) {
    const input = (c as { input?: unknown }).input;
    if (typeof input === 'object' && input !== null) {
      return input as Record<string, unknown>;
    }
  }
  return {};
}

export function ConversationList({
  agentId,
  messages,
  onEditMessage,
  onResendMessage,
}: ConversationListProps) {
  const { t } = useTranslation();
  const partialText = useAppStore((s) => s.partialTextByAgent[agentId]);
  const partialActive = partialText !== undefined;
  // Sprint 17 — single in-flight indicator. PartialIndicator wins on
  // text streaming (the content forming IS the signal); LiveActivity
  // covers the gap during thinking + tool execution.
  const liveActivity = useAppStore((s) => s.liveActivityByAgent[agentId]);
  // Sprint working-signal-ssot (spec D6) — third tier: when both
  // fine-grained signals are silent but the task is not done (derived
  // `working`), the WorkingFallback line keeps the conversation honest.
  const agent = useAppStore((s) => s.agents[agentId]);

  // Sprint work-segment-collapse (PR-B, D7) — one selector each, never
  // inside the turns.map below (hook rules).
  const expandedSegments = useAppStore((s) => s.expandedSegmentsByAgent[agentId]);
  const toggleSegmentExpanded = useAppStore((s) => s.toggleSegmentExpanded);

  // Sprint orphan-tool-rows-honest (D013-EC-147, spec D1/D3) — per-row
  // plausibly-live context. One ordered pass: a tool_use's turn is
  // provably CLOSED iff a turn-closing system event (`result` / `exit`,
  // or the `turn_boundary` that opens the next turn) appears after it.
  // Combined with the derived `agent.working` signal in ctxForMessage
  // below; every row surface and the aggregation pass consume the SAME
  // rule via founder-layer.ts.
  //
  // Gate-2 correction (cycle 1): the first draft keyed staleness off "a
  // later user message". Mid-turn sends are first-class (the composer
  // always accepts input and the user row persists immediately, BEFORE
  // the still-executing tool finishes), so that proxy would have flipped
  // live rows to "interrupted" the moment the founder typed.
  // Sprint subagent-launch-ack-liveness (B3) — extended IN PLACE, same pass, to also produce
  // `staleLaunchIds`: ids of LAUNCH messages with more than one HARD turn close (`result`/
  // `exit`, `TURN_CLOSING_SUBTYPES` — NOT `turn_boundary`, which opens the next turn rather
  // than closing this one) after them in the stream. One survived close is legitimate
  // (D013-EC-137: a background child outlives its own turn by design); two means it also
  // survived the FOLLOWING turn whole, and the honest state there is "no completion signal",
  // not "working" (closes the 26% of D013-EC-169 that never notifies).
  const { turnClosedAfterById, staleLaunchIds } = useMemo(() => {
    let lastCloseIdx = -1;
    const hardCloseIdx: number[] = [];
    messages.forEach((m, i) => {
      if (m.type !== 'system') return;
      const subtype = (m.content as { subtype?: unknown } | null)?.subtype;
      // Sprint turn-collapse-final-answer — the three subtypes used to be
      // inlined here while `turn-boundaries.ts` kept its own copy under a
      // comment claiming one shared home. Gate 2 caught the drift; the set
      // now has one owner, and it is deliberately WIDER than the collapse
      // gate's (see that module's header for why `turn_boundary` proves
      // staleness but not closure).
      if (typeof subtype !== 'string') return;
      if (TURN_STALENESS_SUBTYPES.has(subtype)) lastCloseIdx = i;
      if (TURN_CLOSING_SUBTYPES.has(subtype)) hardCloseIdx.push(i);
    });
    const closed = new Map<string, boolean>();
    const stale = new Set<string>();
    messages.forEach((m, i) => {
      if (m.type !== 'tool_use') return;
      closed.set(m.id, lastCloseIdx > i);
      let after = 0;
      for (const idx of hardCloseIdx) {
        if (idx > i) {
          after += 1;
          if (after > 1) break;
        }
      }
      if (after > 1) stale.add(m.id);
    });
    return { turnClosedAfterById: closed, staleLaunchIds: stale };
  }, [messages]);
  const agentWorking = agent?.working ?? false;
  const ctxForMessage = useCallback(
    (m: Message): RowLivenessCtx => ({
      agentWorking,
      turnClosedAfter: turnClosedAfterById.get(m.id) ?? false,
    }),
    [agentWorking, turnClosedAfterById],
  );

  const { resultByToolUseId, pairedResultIds } = useMemo(
    () => pairToolResults(messages),
    [messages],
  );

  const { topLevel, childrenByParent, orphanNoticeIds, orphanIds } = useMemo(
    () => groupBySubagent(messages),
    [messages],
  );

  // D25 — show model badges only when the agent has used 2+ distinct models.
  const modelBadgeActive = useMemo(() => {
    return distinctModelsInHistory(messages).size >= 2;
  }, [messages]);

  // S6.5 — index permission_request / ask_user_question by request_id so we
  // render ONE card per request even though there are 2+ messages per
  // request_id (pending → terminal). Also identify the chronologically
  // earliest pending request id so that — at most — one card is sticky.
  const permissionsByRequestId = useMemo(() => {
    const map = new Map<
      string,
      {
        pending?: PermissionPendingContent;
        ask?: AskUserQuestionPendingContent;
        terminal?: PermissionTerminalContent;
        // The original `pending` message id, used as the React key + for
        // routing in the renderer below. We render at the position of the
        // pending message to keep stream order intact.
        pendingMessageId?: string;
      }
    >();
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      const rid = getRequestId(m.content);
      if (!rid) continue;
      const entry = map.get(rid) ?? {};
      if (isAskUserQuestionPending(m.content)) {
        entry.ask = m.content;
        entry.pendingMessageId = m.id;
      } else if (isPermissionPending(m.content)) {
        entry.pending = m.content;
        entry.pendingMessageId = m.id;
      } else if (isPermissionTerminal(m.content)) {
        entry.terminal = m.content;
      }
      map.set(rid, entry);
    }
    return map;
  }, [messages]);

  // First (chronologically earliest) pending request id with no terminal
  // yet — that's the one that gets the sticky pin. Multi-pending uses
  // stream-order; subsequent cards render inline without sticky.
  const stickyRequestId = useMemo(() => {
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      const rid = getRequestId(m.content);
      if (!rid) continue;
      const entry = permissionsByRequestId.get(rid);
      if (entry && !entry.terminal && (entry.pending || entry.ask)) return rid;
    }
    return null;
  }, [messages, permissionsByRequestId]);

  // Set of message ids to SKIP in the normal render pass. Terminal
  // permission rows are rendered (as PermissionOutcomePill) by the SAME
  // slot as the pending row, so we hide the duplicate terminal message.
  const hiddenMessageIds = useMemo(() => {
    const hidden = new Set<string>();
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      if (isPermissionTerminal(m.content)) hidden.add(m.id);
    }
    return hidden;
  }, [messages]);

  // Sprint work-segment-collapse (PR-B, D6(c)) — a segment holding a
  // permission / ask_user_question card with NO terminal outcome yet is
  // never foldable. The terminal message that would prove a card resolved
  // is filtered out of the render set by `hiddenMessageIds` above, so
  // `isPermissionPending` alone would stay true forever for an ANSWERED
  // card too — this set is the deviation `splitWorkSegments`'s 4th
  // argument documents (turn-collapse-final-answer D3's
  // `unresolvedPermissionMessageIds`, restored keyed the same way).
  const unresolvedCardMessageIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of permissionsByRequestId.values()) {
      if (!e.terminal && (e.pending || e.ask) && e.pendingMessageId) s.add(e.pendingMessageId);
    }
    return s;
  }, [permissionsByRequestId]);

  // Sprint subagent-launch-ack-liveness (D3) — UNA derivación, DOS consumidores
  // (SubagentPanel recursivo y el 5º argumento de splitWorkSegments). `unresolvedCardMessageIds`
  // (arriba) ya significa "hay una tarjeta sin resolver": se reutiliza tal cual (C13).
  const bgCtx = useMemo<BackgroundLaunchCtx>(
    () => ({
      completionByToolUseId: pairTaskNotifications(messages),
      staleLaunchIds,
      parentBlockedOnCard: unresolvedCardMessageIds.size > 0,
      agentWorking,
    }),
    [messages, staleLaunchIds, unresolvedCardMessageIds, agentWorking],
  );

  // Sprint 12 (D-008) — hide system lifecycle messages in founder mode.
  const developerMode = useSettingsStore((s) => s.developerMode);

  // Campaña agent-noise-collapse — UNA pasada, DOS consumidores: el filtro
  // de `renderItems` (las filas `hide`) y el despacho de `renderItem` (las
  // filas `report`). Calcularlo dos veces sería recorrer 37 informes de
  // 16 KB dos veces por render.
  //
  // VACÍO EN MODO DEVELOPER, a propósito: ahí la verdad cruda se conserva
  // byte a byte (founder-first §3 — la capa developer es el diagnóstico
  // cuando algo va mal, y un informe plegado no sirve para diagnosticar).
  // Un `Map` vacío hace que las dos ramas de abajo sean no-ops sin un solo
  // `if (developerMode)` extra en el camino caliente.
  const noiseByMessageId = useMemo(() => {
    const map = new Map<string, NoiseVerdict>();
    if (developerMode) return map;
    for (const m of topLevel) {
      const verdict = classifyNoiseRow(m, orphanIds.has(m.id));
      if (verdict !== null) map.set(m.id, verdict);
    }
    return map;
  }, [topLevel, orphanIds, developerMode]);

  // Sprint 6 (Phase 2, D48) — aggregation. Filter out paired tool_results
  // BEFORE aggregating so they don't break runs of consecutive tool
  // calls (the aggregation pass treats any non-tool_use as a run-breaker).
  // S6.5 — also filter out the duplicate terminal permission rows; their
  // content is rendered by the pending message's slot (collapsed to
  // PermissionOutcomePill).
  // Sprint 12 — also filter out system lifecycle messages in founder mode.
  const renderItems = useMemo(() => {
    const filtered = topLevel.filter((m) => {
      if (!developerMode && m.type === 'system') {
        const content = m.content as { subtype?: string } | null;
        const subtype = content?.subtype;
        if (typeof subtype === 'string' && HIDDEN_SYSTEM_SUBTYPES.has(subtype)) {
          return false;
        }
      }
      if (m.type === 'tool_result' && pairedResultIds.has(m.id)) return false;
      if (hiddenMessageIds.has(m.id)) return false;
      // Sprint d028-workflow-agents (D5) — a `workflow_progress` row is
      // read ONLY through `childrenByParent` (WorkflowPanel), never
      // rendered as a timeline item. Unconditional (not gated on
      // developerMode): `MessageBubble` returns `null` for this type
      // regardless, but the dispatch default still emits a wrapper
      // `<div data-message-id>` for a `null`-rendering bubble
      // (`ConversationList.tsx`'s own dispatch below), producing the
      // D-msg-6 phantom `space-y` gap. Reachable only for an ORPHANED row
      // (a child of a present parent never reaches `topLevel` at all,
      // `messages.ts` groupBySubagent) — bounded, self-healing on an old
      // SPA once it rolls (D5, pre-mortem #5).
      if (m.type === 'workflow_progress') return false;
      // Sprint subagent-launch-ack-liveness (D5, D013-EC-659, FM7 fix round — QA) — same
      // D-msg-6 phantom-gap class as `workflow_progress` above, same fix: UNCONDITIONAL, not
      // gated on `developerMode`. `HIDDEN_SYSTEM_SUBTYPES` above only applies `if
      // (!developerMode …)`, so with developer mode ON a root-level `task_notification`
      // message fell through to the generic dispatch default, which wraps ANY message in a
      // `<div data-message-id>` regardless of whether `MessageBubble`/`SystemPill` render
      // something inside it — and `SystemPill` has no `case 'task_notification'`, so it
      // rendered `null` inside a real wrapper row: one empty flex gap per settled background
      // child, every time developer mode is on. D5's intent ("nunca píldora") is unconditional
      // on mode; this filter makes the code match that intent instead of relying on
      // `HIDDEN_SYSTEM_SUBTYPES` doing double duty as both "hidden in founder mode" (its real
      // job) and "never rendered at all" (`workflow_progress`'s job, which this subtype needed
      // too).
      if (
        m.type === 'system' &&
        (m.content as { subtype?: unknown } | null)?.subtype === 'task_notification'
      ) {
        return false;
      }
      // Sprint work-segment-collapse (D5, D013-EC-522) — `thinking_block`
      // rows are dropped here in founder mode: the CLI on Claude Max emits
      // no thinking text, so the "Pensó durante N segundos" row disclosed
      // nothing (Santi 2026-09-10). Two consequences, both intended:
      // founder mode loses the rows AND its tool runs no longer break on
      // them (aggregate.ts's run-breaker rule now only ever fires on real
      // non-tool items there); developer mode is unchanged — thinking rows
      // still render and still break runs, which is correct since the rows
      // are visible there.
      if (!developerMode && m.type === 'thinking_block') return false;
      // Campaña agent-noise-collapse (resultados 2 y 3) — el turno vacío
      // ("No response requested.") y la plomería huérfana de un ayudante
      // cuyo panel ya no está cargado. ANTES de `aggregateMessages`: si se
      // filtrasen después, un turno vacío en medio de dos tool_use
      // rompería la agrupación y dejaría dos tarjetas donde debería haber
      // una — el mismo defecto que §4 de founder-first existe para matar.
      if (noiseByMessageId.get(m.id)?.kind === 'hide') return false;
      return true;
    });
    return aggregateMessages({
      topLevel: filtered,
      resultByToolUseId,
      ctxForMessage,
    });
  }, [topLevel, pairedResultIds, resultByToolUseId, hiddenMessageIds, developerMode, ctxForMessage, noiseByMessageId]);

  // Sprint 6 (Phase 3, D49) — synthesize per-turn summary cards keyed by the
  // `system_event` `result` message that closed each turn. Pure render-time
  // projection; never persisted.
  const summariesByResultId = useMemo(
    () => synthesizeTurnSummaries(messages),
    [messages],
  );

  // Sprint 6 (Phase 5, D39) — derive plan progress from the latest TodoWrite.
  const progress = useMemo(() => deriveProgress(messages), [messages]);

  // Sprint sticky-message-section-stacking-cursor-parity — group the
  // flat renderItems into per-turn buckets. A "turn" starts at any
  // user message and contains every render item up to (but not
  // including) the next user message. The first bucket (if it holds
  // any items before the first user message) is keyed `__lead__` and
  // does not contain a sticky child. Each bucket gets its own
  // `<section data-turn-id>` wrapper that becomes the CSS sticky
  // containing block for the user message inside it — that's what
  // produces the natural push-out behaviour as one section's bottom
  // edge meets the next section's top edge during scroll.
  const turns = useMemo(() => groupIntoTurns(renderItems), [renderItems]);

  // Per-turn closure / status / duration / collapse eligibility (D2, D3,
  // D5). Derived from the RAW message stream, not from renderItems: the
  // closing `result` / `exit` events are filtered out of the founder-mode
  // render set, and they are exactly what proves a turn is over.
  const turnMetaById = useMemo(
    () =>
      deriveTurnMeta({
        turns,
        messages,
        agentWorking,
      }),
    [turns, messages, agentWorking],
  );

  // Sprint work-segment-collapse (PR-B, D1/D2/D3/D6) — one derivation, three
  // consumers (the anchor signature, the anchor's folded-id quorum, the
  // JSX below) — SSoT, replaces `splitTurnItems`'s single `head`-only call.
  const splitByTurnId = useMemo(() => {
    const m = new Map<string, ReturnType<typeof splitWorkSegments>>();
    for (const t of turns) {
      m.set(
        t.turnId,
        splitWorkSegments(t.items, turnMetaById.get(t.turnId), resultByToolUseId, unresolvedCardMessageIds, bgCtx),
      );
    }
    return m;
  }, [turns, turnMetaById, resultByToolUseId, unresolvedCardMessageIds, bgCtx]);

  // B3 — carries `fold` alongside `foldable`: a bare-size segment (D3)
  // folds nothing and emits no `data-segment-id`, so it must not enter the
  // anchor's folded-id quorum below even though it IS `foldable`.
  const segmentEntries = useMemo(() => {
    const out: { key: string; foldable: boolean; fold: boolean }[] = [];
    for (const t of turns) {
      for (const p of splitByTurnId.get(t.turnId)?.parts ?? []) {
        if (p.kind === 'segment') out.push({ key: p.segment.key, foldable: p.segment.foldable, fold: p.segment.fold });
      }
    }
    return out;
  }, [turns, splitByTurnId]);

  // Sprint compact-action-rows (D8) — the anchor's trigger moved from
  // turn-collapse (retired by D7) to group-collapse: every time a live
  // action settles it leaves its standalone row and joins a group, so the
  // list loses a row mid-stream. Same height-jump class, new source.
  // Sprint work-segment-collapse (PR-B, D8, B10) — a segment folding mid-
  // stream is the SAME class of jump, so the signature grows a
  // `seg-<key>:<foldable>` token per segment (`buildCollapseSignature`) —
  // the DERIVED `foldable`, never `fold` and never the founder's expanded
  // state (challenger item 5: a signature that moved on a manual toggle
  // would make the anchor's only bottom action scroll past the rows just
  // revealed).
  const collapsedSignature = useMemo(
    () => buildCollapseSignature(renderItems, segmentEntries),
    [renderItems, segmentEntries],
  );

  // Sprint work-segment-collapse (PR-B, D8, B3) — ids of segments that
  // ACTUALLY render a `WorkSegment` root this render (`fold === true`), in
  // document order. Consumed by the anchor's above-viewport delta branch.
  const foldedSegmentIds = useMemo(
    () => segmentEntries.filter((s) => s.fold).map((s) => s.key),
    [segmentEntries],
  );

  // Sprint 6 (Phase 5, D44) — diff pills per TodoWrite emit (skip the first;
  // first emit has no prior to diff against). Keyed by the TodoWrite message
  // id so MessageBubble can pass them through to the TodoChecklist render.
  const todoPillsByMessageId = useMemo(() => {
    const out = new Map<string, import('../lib/tool-rendering/diff-todos').TodoUpdatePill[]>();
    const emits = collectTodoEmitsLocal(messages);
    for (let i = 1; i < emits.length; i += 1) {
      const prev = emits[i - 1];
      const cur = emits[i];
      if (!prev || !cur) continue;
      const pills = diffTodos(parseTodos(getTodoInput(prev)), parseTodos(getTodoInput(cur)));
      if (pills.length > 0) out.set(cur.id, pills);
    }
    return out;
  }, [messages]);

  const renderCount = renderItems.length + (partialActive ? 1 : 0);

  // Sprint 15 — tail marker lets useChatScroll skip the pill bump + auto-
  // scroll when growth comes from prepend (loadOlderMessages) instead of
  // appendMessage. Otherwise paginating up would spuriously fire
  // "↓ N new messages" and try to scroll the user to the bottom.
  const tailId = messages[messages.length - 1]?.id ?? null;

  const { containerRef, newCount, scrollToBottom, isUserScrolledUp } = useChatScroll({
    agentId,
    messageCount: renderCount,
    partialActive,
    tailId,
  });

  // Sprint turn-collapse-final-answer (D8) — a turn closing removes
  // thousands of pixels in one frame. This keeps the founder where they
  // were: pinned to the bottom with the answer in view, or anchored to
  // whatever they were reading further up.
  // Sprint work-segment-collapse (PR-B, D8) — `foldedSegmentIds`/`tailId`/
  // `headId` land together with the hook's own signature update (phase 5).
  useCollapseAnchor({
    scrollContainerRef: containerRef,
    signature: collapsedSignature,
    foldedSegmentIds,
    tailId,
    // NB-7 — same source `useInfiniteScrollUp` uses below: changed ⇒ this
    // really was a prepend, not a fold (D8 prepend-insensitivity).
    headId: messages[0]?.id ?? null,
    userScrolledUp: isUserScrolledUp,
  });

  // Sprint 15 — lazy upward pagination + anchor scroll. Fires loadOlder
  // when the user scrolls within 200px of the top; restores the visual
  // scroll position after the prepend lands.
  const { hasMore, loadingOlder, loadOlder } = usePaginatedHistory(agentId);
  useInfiniteScrollUp({
    scrollContainerRef: containerRef,
    hasMore,
    loadingOlder,
    loadOlder,
    headId: messages[0]?.id ?? null,
  });

  // Sprint chatgpt-user-bubble (D-036 D4) — `UserMessage` is no longer
  // `position: sticky`; the last user message scrolls away like any
  // chat message instead of docking at the top of its turn.

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-auto"
        data-testid="message-list"
        data-agent-id={agentId}
      >
        <StickyProgressBar progress={progress} />
        <div
          className="max-w-reading mx-auto px-3 md:pl-6 md:pr-12 py-4 space-y-4"
          data-testid="message-column"
        >
          {/* Sprint 15 — lazy upward pagination spinner. Renders inline at
              the top so the anchor-scroll layout-effect treats it as part
              of the prepended block. Only visible while loadOlderMessages
              is in flight AND there are more messages to fetch. */}
          {hasMore && loadingOlder && (
            <div
              className="text-center text-body text-muted-foreground/70 py-2"
              data-testid="history-loading-older"
            >
              {t('chatView.loadingOlder')}
            </div>
          )}
          {topLevel.length === 0 && !partialActive && (
            <div className="text-center text-body text-muted-foreground/70 py-12">
              {t('chatView.noMessagesYet')}
            </div>
          )}

          {turns.map((turn) => {
            // Sprint work-segment-collapse (PR-B) — a closed turn (or one
            // the agent has already spoken past) hides its WORK SEGMENTS
            // behind header lines (D1/D2); every agent message and the
            // founder's own message render outside any segment, in stream
            // order. `meta` decides whether the trailing segment may fold
            // at all (D2b); `splitByTurnId` decides the per-segment fold
            // state (D2a/D3/D6).
            const meta = turnMetaById.get(turn.turnId);
            const { head, parts } = splitByTurnId.get(turn.turnId) ?? { head: null, parts: [] };
            // D4 — the duration lives in exactly one place per turn: on the
            // segment headers when the turn has at least one (`fold`), on
            // the turn line when it has none.
            const headerCount = parts.filter((p) => p.kind === 'segment' && p.segment.fold).length;
            const openFor = (seg: WorkSegmentData): boolean =>
              expandedSegments?.[seg.key] ?? (developerMode || seg.defaultExpanded);
            const renderItem = (item: RenderItem): ReactNode => {
                // S6.5 — permission cards intercept BEFORE aggregation/turn-
                // summary logic so they always own the slot they live in.
                if (item.kind === 'single') {
                  const m = item.message;
                  if (m.type === 'permission_request' || m.type === 'ask_user_question') {
                    const rid = getRequestId(m.content);
                    if (rid) {
                      const entry = permissionsByRequestId.get(rid);
                      if (entry?.ask) {
                        return (
                          <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                            <AskUserQuestionCard
                              agentId={agentId}
                              pending={entry.ask}
                              terminal={entry.terminal}
                              sticky={!entry.terminal && rid === stickyRequestId}
                            />
                          </div>
                        );
                      }
                      if (entry?.pending) {
                        return (
                          <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                            <PermissionCard
                              agentId={agentId}
                              pending={entry.pending}
                              terminal={entry.terminal}
                              sticky={!entry.terminal && rid === stickyRequestId}
                            />
                          </div>
                        );
                      }
                    }
                    // Defensive: malformed permission_request with no
                    // request_id. Render nothing rather than blow up.
                    return null;
                  }
                }
                if (item.kind === 'aggregation') {
                  return (
                    <div
                      key={item.key}
                      className="flex flex-col"
                      data-message-id={item.key}
                    >
                      <AggregationCard
                        item={item}
                        resultByToolUseId={resultByToolUseId}
                        ctxForMessage={ctxForMessage}
                      />
                    </div>
                  );
                }
                const m = item.message;
                // Campaña agent-noise-collapse (resultados 1 y 2) — un
                // informe interno o un aviso del sistema es UNA fila
                // plegada, nunca el muro crudo. Intercepta ANTES del
                // aviso de huérfano y del despacho genérico: en modo
                // founder la fila plegada SUSTITUYE a la burbuja, y el
                // `OrphanSubagentNotice` es developer-only por diseño
                // (§7 de ai-elements-rendering), así que no lo acompaña.
                const noise = noiseByMessageId.get(m.id);
                if (noise?.kind === 'report') {
                  return (
                    <InternalReportRow
                      key={m.id}
                      messageId={m.id}
                      classification={noise.classification}
                      raw={noise.raw}
                    />
                  );
                }
                // Sprint ai-elements-rendering (spec §7) — orphan child:
                // parent_tool_use_id points at a pruned/truncated parent.
                // The message renders at root; the first of each orphan
                // group gets a dev-only notice.
                const orphanNotice = orphanNoticeIds.has(m.id) ? (
                  <OrphanSubagentNotice />
                ) : null;
                // Sprint dispatch-lineage-ui (D4b) — a `dispatch_progress`
                // row (top-level, one per `/dispatch` this conversation
                // made) renders as the worker's card through SubagentPanel.
                // Routed BEFORE the tool_use switch: it is not a tool row.
                if (m.type === 'dispatch_progress') {
                  return (
                    <div
                      key={m.id}
                      className="flex flex-col"
                      data-message-id={m.id}
                    >
                      {orphanNotice}
                      <DispatchWorkerCard message={m} />
                    </div>
                  );
                }
                // Subagent (Agent / Task) → render the recursive tree
                // (sprint ai-elements-rendering: the panel receives the
                // full childrenByParent map and recurses on nested
                // Task/Agent calls itself).
                if (m.type === 'tool_use') {
                  const tu = asToolUse(m);
                  // Sprint d028-workflow-agents (D2) — a `Workflow` launch
                  // routes to WorkflowPanel, NOT SubagentPanel: it carries
                  // a `workflow_progress` snapshot row (D1), never
                  // sidechain messages. Same four props SubagentPanel
                  // already receives (R4-m7); `ctxForMessage` is called
                  // by the panel on the LAUNCH message itself (R2-B1).
                  if (WORKFLOW_TOOL_NAMES.has(tu.name) && tu.tool_use_id) {
                    const result = resultByToolUseId.get(tu.tool_use_id);
                    return (
                      <div
                        key={m.id}
                        className="flex flex-col"
                        data-message-id={m.id}
                      >
                        {orphanNotice}
                        <WorkflowPanel
                          toolUseMessage={m}
                          childrenByParent={childrenByParent}
                          resultMessage={result}
                          ctxForMessage={ctxForMessage}
                        />
                      </div>
                    );
                  }
                  if (SUBAGENT_TOOL_NAMES.has(tu.name) && tu.tool_use_id) {
                    const result = resultByToolUseId.get(tu.tool_use_id);
                    return (
                      <div
                        key={m.id}
                        className="flex flex-col"
                        data-message-id={m.id}
                      >
                        {orphanNotice}
                        <SubagentPanel
                          toolUseMessage={m}
                          childrenByParent={childrenByParent}
                          resultMessage={result}
                          ctxForMessage={ctxForMessage}
                          bgCtx={bgCtx}
                        />
                      </div>
                    );
                  }
                  const pairedResult = tu.tool_use_id
                    ? resultByToolUseId.get(tu.tool_use_id)
                    : undefined;
                  return (
                    <div
                      key={m.id}
                      className="flex flex-col"
                      data-message-id={m.id}
                    >
                      {orphanNotice}
                      <MessageBubble
                        message={m}
                        toolResultMessage={pairedResult}
                        showModelBadge={modelBadgeActive}
                        todoUpdatePills={todoPillsByMessageId.get(m.id)}
                        toolLivenessCtx={ctxForMessage(m)}
                      />
                    </div>
                  );
                }
                // User messages render bare (no flex-col wrapper) so
                // their own `position: sticky` is bounded by the
                // surrounding `<section>` rather than a wrapper div.
                if (m.type === 'user') {
                  return (
                    <MessageBubble
                      key={m.id}
                      message={m}
                      showModelBadge={modelBadgeActive}
                      onEditMessage={onEditMessage}
                      onResendMessage={onResendMessage}
                      agentId={agentId}
                    />
                  );
                }
                return (
                  <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                    {orphanNotice}
                    <MessageBubble
                      message={m}
                      showModelBadge={modelBadgeActive}
                    />
                  </div>
                );
            };

            // Sprint sticky-message-section-stacking-cursor-parity —
            // every turn renders as its own `<section>`, consumed by
            // `deriveTurnMeta`, turn collapse and search (D-036 D4
            // keeps the sections; only the user message's own sticky
            // positioning was retired). The `__lead__` section holds
            // anything that rendered before the first user message
            // (system pills, etc.).
            return (
              <section
                key={turn.turnId}
                data-turn-id={turn.turnId}
                className="space-y-4"
              >
                {/* Sprint work-segment-collapse (PR-B) — the section
                    renders in STREAM ORDER: the founder's message, the
                    turn line (only when it has something to say, D4),
                    then head → [prose | segment | loud pill | compaction
                    divider]* (D1/D6). Loud pills and the divider are
                    BREAKERS in `splitWorkSegments`, so they keep their
                    chronological position and render as loose `{kind:
                    'item'}` parts here, never inside a panel. */}
                {head ? renderItem(head) : null}
                {meta?.closed ? (
                  <TurnCollapse
                    meta={meta}
                    verifications={
                      meta.closingMessageId
                        ? summariesByResultId.get(meta.closingMessageId)?.verifications
                        : undefined
                    }
                    duration={headerCount === 0 ? formatWorkedFor(meta.durationMs, t) : null}
                  />
                ) : null}
                {parts.map((part) =>
                  part.kind === 'item' ? (
                    renderItem(part.item)
                  ) : part.segment.fold ? (
                    <WorkSegment
                      key={`seg-${part.segment.key}`}
                      segment={part.segment}
                      open={openFor(part.segment)}
                      onToggle={() =>
                        toggleSegmentExpanded(agentId, part.segment.key, openFor(part.segment))
                      }
                    >
                      {part.segment.items.map(renderItem)}
                    </WorkSegment>
                  ) : (
                    // D3 bare-size rule — a segment whose one item is a
                    // single row renders bare into the section: no header,
                    // no wrapper element (a keyed Fragment emits no DOM),
                    // which is also what D6(c)'s "no wrapper at all"
                    // guarantee for a pending permission card needs.
                    <Fragment key={`seg-${part.segment.key}`}>
                      {part.segment.items.map(renderItem)}
                    </Fragment>
                  ),
                )}
              </section>
            );
          })}

          {partialActive && <PartialIndicator text={partialText ?? ''} />}
          {!partialActive && liveActivity && <LiveActivity activity={liveActivity} />}
          {/* Campaña agent-noise-collapse (resultado 4) — `|| isAwaitingHelpers`.
              `agent.working` sale de `deriveWorking`, que mira la tabla
              `background_tasks`; el rollup cuenta los jobs `kind:'subagent'`.
              Son dos fuentes distintas y la segunda puede tener hijos fuera
              con la primera en `false` — y ése es justo el hueco en el que
              la cola de la conversación se quedaba muda. */}
          {!partialActive && !liveActivity && (agent?.working || isAwaitingHelpers(agent)) && agent && (
            <WorkingFallback agent={agent} />
          )}
          {/* Sprint 20.3 PR-7 (D013-EC-22) — live-stream truncated pill.
              Renders only when the per-agent backpressure cap was hit
              this turn. The DB has the persisted message; clicking
              reloads from the server. */}
          <StreamTruncatedPill agentId={agentId} />
        </div>
      </div>

      <MessageRail key={agentId} messages={messages} scrollContainerRef={containerRef} />
      <ScrollToBottomPill
        count={newCount}
        visible={isUserScrolledUp}
        onClick={() => scrollToBottom(true)}
      />
    </div>
  );
}

// Sprint ai-elements-rendering (spec §7) — dev-only marker above the
// first message of an orphan subagent group. The founder never sees it
// (protocol breakage is developer-shaped); a developer debugging a
// truncated history gets the signal.
function OrphanSubagentNotice() {
  const { t } = useTranslation();
  return (
    <DeveloperOnly>
      <div
        className="text-caption text-muted-foreground/70 mb-1"
        data-testid="orphan-subagent-notice"
      >
        {t('subagent.orphanNotice')}
      </div>
    </DeveloperOnly>
  );
}

interface Turn {
  /**
   * Stable key for the section. `__lead__` for pre-conversation content
   * (system pills before any user input); the user message id
   * otherwise.
   */
  turnId: string;
  items: RenderItem[];
}

/**
 * Split the flat render-item list into per-turn sections. A turn boundary
 * is any item whose underlying message has `type === 'user'`. The first
 * bucket — anything before the first user message — gets the synthetic
 * id `__lead__` and renders without a sticky child.
 *
 * Sprint sticky-message-section-stacking-cursor-parity.
 */
function groupIntoTurns(items: RenderItem[]): Turn[] {
  const turns: Turn[] = [];
  let current: Turn = { turnId: LEAD_TURN_ID, items: [] };
  for (const it of items) {
    const isUser = it.kind === 'single' && it.message.type === 'user';
    if (isUser) {
      // Close the lead bucket only if it actually accumulated content;
      // an empty `__lead__` would add a stray empty section.
      if (current.items.length > 0 || current.turnId !== LEAD_TURN_ID) {
        turns.push(current);
      }
      current = { turnId: it.message.id, items: [it] };
    } else {
      current.items.push(it);
    }
  }
  if (current.items.length > 0) turns.push(current);
  return turns;
}
